/**
 * 幾何運算工具函式
 */

import type { Point, Rectangle, Line } from '@/types';

// ========================================================================
// 點運算
// ========================================================================

/**
 * 計算兩點之間的距離
 */
export function distance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 計算兩點之間的角度（弧度）
 */
export function angle(p1: Point, p2: Point): number {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

/**
 * 計算兩點的中點
 */
export function midpoint(p1: Point, p2: Point): Point {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

/**
 * 將點繞原點旋轉
 */
export function rotatePoint(point: Point, angleRad: number, center: Point = { x: 0, y: 0 }): Point {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/**
 * 縮放點
 */
export function scalePoint(point: Point, scale: number, center: Point = { x: 0, y: 0 }): Point {
  return {
    x: center.x + (point.x - center.x) * scale,
    y: center.y + (point.y - center.y) * scale,
  };
}

/**
 * 平移點
 */
export function translatePoint(point: Point, dx: number, dy: number): Point {
  return {
    x: point.x + dx,
    y: point.y + dy,
  };
}

// ========================================================================
// 線段運算
// ========================================================================

/**
 * 計算線段長度
 */
export function lineLength(line: Line): number {
  return distance(line.start, line.end);
}

/**
 * 計算線段中點
 */
export function lineMidpoint(line: Line): Point {
  return midpoint(line.start, line.end);
}

/**
 * 計算線段角度（弧度）
 */
export function lineAngle(line: Line): number {
  return angle(line.start, line.end);
}

/**
 * 計算點到線段的最短距離
 */
export function pointToLineDistance(point: Point, line: Line): number {
  const { start, end } = line;
  const lineLen = lineLength(line);

  if (lineLen === 0) {
    return distance(point, start);
  }

  // 計算投影參數
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) /
        (lineLen * lineLen)
    )
  );

  // 計算最近點
  const nearest: Point = {
    x: start.x + t * (end.x - start.x),
    y: start.y + t * (end.y - start.y),
  };

  return distance(point, nearest);
}

// ========================================================================
// 矩形運算
// ========================================================================

/**
 * 檢查點是否在矩形內
 */
export function isPointInRect(point: Point, rect: Rectangle): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/**
 * 檢查點是否在旋轉後的矩形內
 */
export function isPointInRotatedRect(
  point: Point,
  rect: Rectangle,
  rotation: number,
  center?: Point
): boolean {
  // 矩形中心
  const rectCenter = center ?? {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };

  // 將點反向旋轉到矩形的局部座標系
  const localPoint = rotatePoint(point, -rotation, rectCenter);

  // 在局部座標系中檢查
  return isPointInRect(localPoint, rect);
}

/**
 * 取得矩形的四個角點
 */
export function getRectCorners(rect: Rectangle): [Point, Point, Point, Point] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

/**
 * 取得旋轉後矩形的四個角點
 */
export function getRotatedRectCorners(
  rect: Rectangle,
  rotation: number,
  center?: Point
): [Point, Point, Point, Point] {
  const rectCenter = center ?? {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };

  const corners = getRectCorners(rect);
  return corners.map((corner) => rotatePoint(corner, rotation, rectCenter)) as [
    Point,
    Point,
    Point,
    Point,
  ];
}

/**
 * 計算矩形面積
 */
export function rectArea(rect: Rectangle): number {
  return rect.width * rect.height;
}

/**
 * 檢查兩個矩形是否相交
 */
export function rectsIntersect(rect1: Rectangle, rect2: Rectangle): boolean {
  return !(
    rect1.x + rect1.width < rect2.x ||
    rect2.x + rect2.width < rect1.x ||
    rect1.y + rect1.height < rect2.y ||
    rect2.y + rect2.height < rect1.y
  );
}

/**
 * 計算兩個矩形的交集
 */
export function rectIntersection(rect1: Rectangle, rect2: Rectangle): Rectangle | null {
  const x = Math.max(rect1.x, rect2.x);
  const y = Math.max(rect1.y, rect2.y);
  const width = Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - x;
  const height = Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - y;

  if (width <= 0 || height <= 0) {
    return null;
  }

  return { x, y, width, height };
}

/**
 * 計算包含所有點的最小矩形
 */
export function boundingRect(points: Point[]): Rectangle {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

// ========================================================================
// 多邊形運算
// ========================================================================

/**
 * 檢查點是否在多邊形內（射線法）
 */
export function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];

    if (pi && pj) {
      if (
        pi.y > point.y !== pj.y > point.y &&
        point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x
      ) {
        inside = !inside;
      }
    }
  }

  return inside;
}

/**
 * 計算多邊形面積（Shoelace 公式）
 */
export function polygonArea(polygon: Point[]): number {
  if (polygon.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const pi = polygon[i];
    const pj = polygon[j];
    if (pi && pj) {
      area += pi.x * pj.y;
      area -= pj.x * pi.y;
    }
  }

  return Math.abs(area) / 2;
}

// ========================================================================
// 角度轉換
// ========================================================================

/**
 * 角度轉弧度
 */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * 弧度轉角度
 */
export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * 標準化角度到 0-360 度
 */
export function normalizeAngle(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

// ========================================================================
// 座標轉換
// ========================================================================

/**
 * 螢幕座標轉 Canvas 座標
 */
export function screenToCanvas(
  screenX: number,
  screenY: number,
  canvas: HTMLCanvasElement
): Point {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    x: (screenX - rect.left) * scaleX,
    y: (screenY - rect.top) * scaleY,
  };
}

/**
 * Canvas 座標轉螢幕座標
 */
export function canvasToScreen(
  canvasX: number,
  canvasY: number,
  canvas: HTMLCanvasElement
): Point {
  const rect = canvas.getBoundingClientRect();
  const scaleX = rect.width / canvas.width;
  const scaleY = rect.height / canvas.height;

  return {
    x: canvasX * scaleX + rect.left,
    y: canvasY * scaleY + rect.top,
  };
}
